import {
  groupBy as _groupBy,
  keyBy as _keyBy,
  maxBy as _maxBy,
  minBy as _minBy,
  orderBy as _orderBy,
  sumBy as _sumBy,
  uniqBy as _uniqBy,
} from 'es-toolkit'
export {
  chunk,
  compact,
  countBy,
  debounce,
  difference,
  differenceBy,
  escape,
  isEqual,
  mapKeys,
  mapValues,
  omit,
  omitBy,
  partition,
  pick,
  pickBy,
  range,
  sum,
  trimStart,
  uniq,
} from 'es-toolkit'
export { get as getPath, max, set as setPath } from 'es-toolkit/compat'

function i<T extends Record<string, unknown>, Fn>(
  getter: Fn | (keyof T & string),
): Fn {
  if (typeof getter === 'string') {
    return ((x: T) => x[getter]) as Fn
  }

  return getter
}

// function wrap<Fn>(fn: Fn)

function asArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value]
}

export function keyBy<T, K extends PropertyKey>(
  arr: readonly T[],
  getKeyFromItem: ((item: T) => K) | (keyof T & string),
): Record<K, T> {
  return _keyBy(arr, i(getKeyFromItem))
}

export function maxBy<T>(
  arr: readonly T[],
  getValue: ((item: T) => number) | (keyof T & string),
): T | undefined {
  return _maxBy(arr, i(getValue))
}

export function minBy<T>(
  arr: readonly T[],
  getValue: ((item: T) => number) | (keyof T & string),
): T | undefined {
  return _minBy(arr, i(getValue))
}

export function uniqBy<T>(
  arr: readonly T[],
  getValue: ((item: T) => number) | (keyof T & string),
): T[] {
  return _uniqBy(arr, i(getValue))
}

export function sumBy<T>(
  arr: readonly T[],
  getValue: ((item: T) => number) | (keyof T & string),
): number {
  return _sumBy(arr, i(getValue))
}

export function groupBy<T, K extends PropertyKey>(
  arr: readonly T[],
  getValue: ((item: T) => K) | (keyof T & string),
): Record<K, T[]> {
  return _groupBy(arr, i(getValue))
}

export function orderBy<T extends object>(
  arr: readonly T[],
  criteria:
    Array<((item: T) => unknown) | keyof T> | ((item: T) => unknown) | keyof T,
  orders: Array<'asc' | 'desc'> | 'asc' | 'desc' = ['asc'],
): T[] {
  criteria = asArray(criteria)
  orders = asArray(orders)

  return _orderBy(arr, criteria, orders)
}

export function isEmpty(value: undefined | null | object | unknown[]) {
  if (!value) return true
  if (Array.isArray(value)) return value.length === 0
  return Object.keys(value).length === 0
}

export function zfill(num: string | number, size = 5) {
  return String(num).padStart(size, '0')
}

export function wait(time: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, time)
  })
}

export function truncate(
  string: string,
  length: number,
  useWordBoundary = true,
): string {
  let result = string.trim()
  const isTooLong = result.length > length
  if (isTooLong) {
    result = string.substring(0, length)
  }
  if (isTooLong && useWordBoundary) {
    result = result.substring(0, result.lastIndexOf(' '))
  }

  return result.trim()
}

export function stripePaymentDescriptor(description: string) {
  // https://docs.stripe.com/get-started/account/statement-descriptors#requirements
  const forbidden = ['<', '>', '\\', "'", '"', '*']

  forbidden.forEach((char) => {
    description = description.replaceAll(char, '')
  })

  return truncate(description, 22)
}

export function deps(..._args: any) {}

export function either(text: string | undefined, alternative: string) {
  if (text && text.trim().length > 0) return text
  return alternative
}

export function line(str: string, char = '=') {
  if (str.length === 0) {
    return Array(80).fill(char).join('')
  }

  const width = (80 - str.length) / 2
  const wl = Math.floor(width - 1)
  const wr = Math.ceil(width - 1)

  return (
    Array(wl).fill(char).join('') +
    ' ' +
    str +
    ' ' +
    Array(wr).fill(char).join('')
  )
}

export function track(..._args: any[]) {
  // noop
}
